import { ClockIcon } from '@heroicons/react/outline';
import { getConversations } from 'api/v2/AI';
import cn from 'classnames';
import { Button } from 'components/atoms/Button';
import { useAIChatContext } from 'components/organisms/AIChat/AIChat.context';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { useInfiniteQuery } from 'react-query';
import type { ConversationHistory } from 'types/ai.types';
import { AICacheKeys } from 'types/cacheKeys.types';

const ConversationCard = ({
  conversation,
  onClick,
  isFirst,
}: {
  conversation: ConversationHistory;
  onClick: () => void;
  isFirst: boolean;
}) => (
  <Button
    onClick={onClick}
    asDefault
    className={cn(
      'w-full py-2 px-4 text-left text-sm hover:opacity-60 transition-opacity',
      !isFirst && 'border-t border-primary-50',
    )}
  >
    {conversation.messages[0].content}
  </Button>
);

const useConversationList = () => {
  const { data } = useInfiniteQuery(
    [AICacheKeys.ConversationsList, AICacheKeys.ConversationsListYearlyReview],
    ({ pageParam = 1 }) =>
      getConversations({
        page: pageParam,
        perPage: 10,
        source: 'wrapped_2024',
      }),
    {
      getNextPageParam: ({ paging: { pagesCount, pageCount, page } }) =>
        pagesCount > page || pageCount > page ? page + 1 : undefined,
    },
  );

  return data?.pages?.flatMap(
    ({ data }) => data,
  ) as unknown as ConversationHistory[];
};

const AIChatYearlyHistory = () => {
  const { setConversationId } = useAIChatContext();

  const [isOpen, setIsOpen] = useState(false);

  const toggleOpen = () => setIsOpen(!isOpen);

  const conversations = useConversationList();

  const handleConversationClick = (conversation: ConversationHistory) => {
    setConversationId(conversation._id);
    setIsOpen(false);
  };

  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!wrapperRef.current) return;
      if (wrapperRef.current.contains(event.target as Node)) return;

      setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!conversations?.length) return null;

  return (
    <div className="relative inline-block z-10" ref={wrapperRef}>
      <Button
        onClick={toggleOpen}
        asDefault
        className={cn(
          'w-9 h-9 flex items-center justify-center rounded-lg border border-primary-100 bg-white shadow-sm text-primary-700 hover:text-primary hover:border-primary hover:ring-2 hover:ring-primary-100 transition-all',
          isOpen && 'text-primary border-primary ring-2 ring-primary-100',
        )}
      >
        <ClockIcon className="w-6 h-6" />
      </Button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, translateY: '95%' }}
            animate={{ opacity: 1, translateY: '100%' }}
            exit={{ opacity: 0, translateY: '95%' }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="max-h-[350px] overflow-y-auto bg-white rounded-10 absolute bottom-[-4px] right-0 translate-y-full border border-primary-50"
          >
            <div className="flex flex-col items-center justify-between py-2">
              {conversations?.map((conversation, index) => (
                <ConversationCard
                  key={conversation._id}
                  conversation={conversation}
                  onClick={() => handleConversationClick(conversation)}
                  isFirst={index === 0}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AIChatYearlyHistory;
